import { useMemo } from 'react';

interface VolatilitySurfaceDataPoint {
  DF: number;
  T: number;
  ATMF: number;
  Future: number;
  DN: number;
  '5C': number;
  '10C': number;
  '15C': number;
  '20C': number;
  '25C': number;
  '30C': number;
  '35C': number;
  '40C': number;
  '5P': number;
  '10P': number;
  '15P': number;
  '20P': number;
  '25P': number;
  '30P': number;
  '35P': number;
  '40P': number;
  '5FLY': number;
  '10FLY': number;
  '15FLY': number;
  '20FLY': number;
  '25FLY': number;
  '30FLY': number;
  '35FLY': number;
  '40FLY': number;
  '5RR': number;
  '10RR': number;
  '15RR': number;
  '20RR': number;
  '25RR': number;
  '30RR': number;
  '35RR': number;
  '40RR': number;
}

interface VolatilitySurfaceData {
  [date: string]: VolatilitySurfaceDataPoint;
}

interface VolatilitySurface3DConfig {
  is3D: boolean;
  data3D: Array<[string, string, number]>;
  xAxisName: string;
  yAxisName: string;
  zAxisName: string;
  min3D: number;
  max3D: number;
  showWireframe: boolean;
  tooltip: {
    xAxisTitle: string;
    yAxisTitle: string;
    zAxisTitle: string;
    xAxisFormatter: (value: any) => string;
    yAxisFormatter: (value: any) => string;
    zAxisFormatter: (value: any) => string;
  };
}

const VOLATILITY_DELTA_KEYS = [
  '5P',
  '10P',
  '15P',
  '20P',
  '25P',
  '30P',
  '35P',
  '40P',
  '45P',
  'ATMF',
  '45C',
  '40C',
  '35C',
  '30C',
  '25C',
  '20C',
  '15C',
  '10C',
  '5C',
];

export const useProcessSurfaceData = (rawData: VolatilitySurfaceData | null) => {
  const processedSurfaceData = useMemo(() => {
    if (!rawData) {
      return [];
    }

    const matrix: Array<[string, string, number]> = [];

    for (const date in rawData) {
      const dataPoint = rawData[date];
      for (const deltaKey of VOLATILITY_DELTA_KEYS) {
        if (dataPoint.hasOwnProperty(deltaKey)) {
          const volatilityValue = dataPoint[deltaKey as keyof VolatilitySurfaceDataPoint] * 100;
          matrix.push([deltaKey, date, volatilityValue]);
        }
      }
    }

    return matrix;
  }, [rawData]);

  const surface3DConfig = useMemo((): VolatilitySurface3DConfig => {
    return {
      is3D: true,
      data3D: processedSurfaceData,
      xAxisName: 'Delta',
      yAxisName: 'Expiry',
      zAxisName: 'Volatility (%)',
      min3D: 10,
      max3D: 80,
      showWireframe: true,
      tooltip: {
        xAxisTitle: 'Delta',
        yAxisTitle: 'Expiry',
        zAxisTitle: 'Vol',
        xAxisFormatter: (value: any) => value?.toString() || '',
        yAxisFormatter: (value: any) => value?.toString() || '',
        zAxisFormatter: (value: any) => `${value?.toFixed(2)}%`,
      },
    };
  }, [processedSurfaceData]);

  return {
    processedData: processedSurfaceData,
    config3D: surface3DConfig,
    hasData: processedSurfaceData.length > 0,
  };
};

export type { VolatilitySurfaceData, VolatilitySurfaceDataPoint, VolatilitySurface3DConfig };
